/**
 * Bonussystemet: gruppens provision (Jonathans spec, docs/bonus/).
 *
 * - Trappan är marginell, aldrig retroaktiv: 35 % på de första 11 800 kr,
 *   40 % på kronorna upp till 27 500 kr och 45 % på kronorna över.
 * - Produkter med fast sats (t.ex. Bankerydspaketet, 50 %) räknas efter de
 *   vanliga och påverkar varken trappan eller nivån.
 *   Provision = trappan(vanliga produkters summa) + Σ fast sats × belopp.
 * - Beloppen är inklusive moms och utan frakt. Säljperioden är gruppens
 *   livstid: en ny period är en ny grupp och börjar på 0 kr.
 * - Allt räknas om från grunden ur ordrarna, aldrig en sparad löpande summa.
 *
 * Samma formel finns i databasen (bonus_tier_commission och
 * create_invoice_secure), som kontrollerar fakturans provision.
 */
import type { Order, OrderItem } from '../types';

export const BONUS_THRESHOLD_1 = 11_800;
export const BONUS_THRESHOLD_2 = 27_500;
export const BONUS_RATES = [0.35, 0.40, 0.45] as const;

/** Standardgissning för snittpris per produkt tills gruppen sålt minst 5 vanliga produkter. */
export const DEFAULT_AVERAGE_PRICE = 150;
export const MIN_ITEMS_FOR_GROUP_AVERAGE = 5;

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Total provision på en summa vanliga produkter, räknad från 0 kr. */
export const tierCommission = (regularSales: number): number => {
    const x = Math.max(0, regularSales);
    const s0 = Math.min(x, BONUS_THRESHOLD_1);
    const s1 = Math.max(0, Math.min(x, BONUS_THRESHOLD_2) - BONUS_THRESHOLD_1);
    const s2 = Math.max(0, x - BONUS_THRESHOLD_2);
    return s0 * BONUS_RATES[0] + s1 * BONUS_RATES[1] + s2 * BONUS_RATES[2];
};

/** Nivån 0, 1 eller 2. Vid exakt tröskeln gäller den nya nivån (som Jonathans aktuell_niva). */
export const bonusLevel = (regularSales: number): 0 | 1 | 2 =>
    regularSales >= BONUS_THRESHOLD_2 ? 2 : regularSales >= BONUS_THRESHOLD_1 ? 1 : 0;

export const levelRate = (level: 0 | 1 | 2) => BONUS_RATES[level];
export const formatRate = (rate: number) => `${Math.round(rate * 100)} %`;

/** Radens fasta sats (sparad på orderraden när ordern lades), annars null = trappan. */
export const fixedRateOf = (item: OrderItem): number | null =>
    typeof item.fixedCommissionRate === 'number' && item.fixedCommissionRate > 0 ? item.fixedCommissionRate : null;

const lineAmount = (item: OrderItem) => item.priceAtMoment * item.quantity;

export interface BonusTotals {
    /** Vanliga produkter (trappan), kr inkl. moms. Avgör nivån. */
    regularSales: number;
    regularItems: number;
    /** Produkter med fast sats. */
    fixedSales: number;
    fixedItems: number;
    fixedCommission: number;
    itemsSold: number;
}

export const emptyTotals = (): BonusTotals => ({ regularSales: 0, regularItems: 0, fixedSales: 0, fixedItems: 0, fixedCommission: 0, itemsSold: 0 });

export const bonusTotals = (orders: Order[]): BonusTotals => {
    const t = emptyTotals();
    for (const o of orders) {
        for (const item of o.items) {
            const rate = fixedRateOf(item);
            const amount = lineAmount(item);
            t.itemsSold += item.quantity;
            if (rate === null) {
                t.regularSales += amount;
                t.regularItems += item.quantity;
            } else {
                t.fixedSales += amount;
                t.fixedItems += item.quantity;
                t.fixedCommission += amount * rate;
            }
        }
    }
    t.regularSales = round2(t.regularSales);
    t.fixedSales = round2(t.fixedSales);
    t.fixedCommission = round2(t.fixedCommission);
    return t;
};

/** Gruppens provision för hela perioden. */
export const groupCommission = (t: Pick<BonusTotals, 'regularSales' | 'fixedCommission'>): number =>
    round2(tierCommission(t.regularSales) + t.fixedCommission);

export interface CommissionResult {
    /** Provisionen på just dessa ordrar (t.ex. en faktura), kr. */
    commission: number;
    regularCommission: number;
    fixedCommission: number;
    /** Snittsatsen på de vanliga produkterna i dessa ordrar. */
    regularRate: number;
    totals: BonusTotals;
}

/**
 * Provisionen på ett urval ordrar, givet vad gruppen redan fakturerat i
 * perioden: trappan(redan + nya) − trappan(redan) + fasta satser. Samma svar
 * oavsett ordning; flera fakturor i perioden summerar till helheten.
 */
export const commissionFor = (orders: Order[], priorRegularSales = 0): CommissionResult => {
    const totals = bonusTotals(orders);
    const regularCommission = round2(tierCommission(priorRegularSales + totals.regularSales) - tierCommission(priorRegularSales));
    return {
        commission: round2(regularCommission + totals.fixedCommission),
        regularCommission,
        fixedCommission: totals.fixedCommission,
        regularRate: totals.regularSales > 0 ? regularCommission / totals.regularSales : levelRate(bonusLevel(priorRegularSales)),
        totals,
    };
};

/**
 * Provisionen per orderrad (för moms per sats och fakturans uppställning).
 * Vanliga rader får snittsatsen för urvalet, fasta rader sin egen sats.
 * Summan kan skilja några öre mot `commission`; den som summerar rättar
 * öresfelet mot totalen.
 */
export const itemCommissionOf = (result: CommissionResult) => (item: OrderItem): number => {
    const rate = fixedRateOf(item);
    return lineAmount(item) * (rate === null ? result.regularRate : rate);
};

export interface NextLevelEstimate {
    nextLevel: 1 | 2 | null;
    nextRate: number | null;
    /** Uppskattning, avrundad uppåt. Aldrig kronor för säljare (Jonathans krav). */
    itemsLeft: number;
    itemsLeftPerSeller: number;
    averagePrice: number;
    /** Bara för admin: kronor kvar till nästa nivå. */
    krLeftInternal: number;
}

/** Snittpris: gruppens eget från 5 sålda vanliga produkter, annars företagets snitt eller 150 kr. */
export const averagePrice = (regularSales: number, regularItems: number, companyAverage?: number | null) =>
    regularItems >= MIN_ITEMS_FOR_GROUP_AVERAGE ? regularSales / regularItems : companyAverage ?? DEFAULT_AVERAGE_PRICE;

/** "Sälj ca X produkter var till, så når ni 40 %". Bara vanliga produkter räknas. */
export const productsToNextLevel = (regularSales: number, regularItems: number, sellers: number, companyAverage?: number | null): NextLevelEstimate => {
    const avg = averagePrice(regularSales, regularItems, companyAverage);
    const level = bonusLevel(regularSales);
    if (level === 2) return { nextLevel: null, nextRate: null, itemsLeft: 0, itemsLeftPerSeller: 0, averagePrice: avg, krLeftInternal: 0 };
    const target = level === 0 ? BONUS_THRESHOLD_1 : BONUS_THRESHOLD_2;
    const krLeft = target - regularSales;
    const itemsLeft = Math.ceil(krLeft / avg);
    return {
        nextLevel: (level + 1) as 1 | 2,
        nextRate: BONUS_RATES[level + 1],
        itemsLeft,
        itemsLeftPerSeller: sellers > 0 ? Math.ceil(itemsLeft / sellers) : itemsLeft,
        averagePrice: round2(avg),
        krLeftInternal: round2(krLeft),
    };
};

export interface RankedSeller<T> {
    seller: T;
    /** 1-baserad placering. Lika antal ger delad placering (1, 2, 2, 4). */
    place: number;
}

/** Topplistan efter antal sålda produkter, aldrig kronor. */
export const rankByItemsSold = <T extends { itemsSold: number }>(sellers: T[]): RankedSeller<T>[] => {
    const sorted = [...sellers].sort((a, b) => b.itemsSold - a.itemsSold);
    return sorted.map((seller, i) => ({
        seller,
        place: sorted.findIndex(s => s.itemsSold === seller.itemsSold) + 1,
    }));
};

/**
 * Vad CoffeeNova har kvar per såld produkt, exklusive moms, efter gruppens
 * provision och inköpet. Inköpspriset är exklusive moms.
 *
 * - Hos säljaren (standard): provisionen är en rabatt på gruppens faktura och
 *   momsen betalas på det som blir kvar.
 * - I kassan (online): CoffeeNova sålde till kunden och betalar moms på hela
 *   priset; provisionen är en kostnad utan moms ovanpå. Kortavgiften ingår inte.
 */
export const marginPerItem = (price: number, purchasePriceExVat: number, vatRate: number, commissionRate: number, online = false): number =>
    online
        ? round2(price / (1 + vatRate / 100) - price * commissionRate - purchasePriceExVat)
        : round2((price * (1 - commissionRate)) / (1 + vatRate / 100) - purchasePriceExVat);

/** Stripes avgift för ett kortköp: 1,5 % + 1,80 kr. */
export const cardFee = (amount: number): number => round2(amount * 0.015 + 1.8);

/** Ordrar som räknas i bonusen: inte borttagna, inte obetalda kassaköp, inte återbetalda. */
export const paymentStatusCounts = (status: string | null | undefined) =>
    status !== 'AWAITING_PAYMENT' && status !== 'REFUNDED';
export const countsForBonus = (o: Order) => !o.deletedAt && paymentStatusCounts(o.paymentStatus);
